import { DeleteOutlined, EditOutlined, GiftOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Form, Input, message, Modal, Select, Space, Table, Tag, Upload, UploadFile, Tour, ConfigProvider } from 'antd';
import type { TourProps } from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { messageService } from '../services/messageService';
import { Message, MessageFilter, RelatedGiftMessageItem, RelatedProduct } from '../types/message';
import { relatedProductService } from '../services/relatedProductService';
import axios from 'axios';
import { giftService } from '../services/giftService';
import { isGiftActive, type Gift } from '../types/gift';
import { configService } from '../services/configService';
import { broadcastService } from '../services/broadcastService';
import { storeService } from '../services/storeService';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import viVN from 'antd/locale/vi_VN';
import { Broadcast } from '../types/broadcast';
import { Store } from '../types/store';

const { Option } = Select;
const { TextArea } = Input;

const BANNER_MAX_SIZE_MB = 1;
const BANNER_MAX_SIZE_BYTES = BANNER_MAX_SIZE_MB * 1024 * 1024;
const BANNER_MAX_SIDE_PX = 1800;
const BANNER_MIN_JPEG_QUALITY = 0.58;
const BANNER_QUALITY_STEP = 0.08;

const COLD_BREW_PRESET = {
    header: 'Tặng Cold Brew mát lạnh cho khách yêu cà phê',
    text: [
        '8AM Coffee gửi bạn một phần quà Cold Brew để bắt đầu ngày mới thật tỉnh táo.',
        'Chọn cơ sở còn quà trong tin nhắn này, sau đó khách có thể mở tin để nhận quà trực tiếp.',
        'Số lượng quà có hạn theo từng cơ sở. Hãy kiểm tra tồn quà trước khi gửi broadcast.'
    ].join('\n'),
    actionUrl: 'https://zalo.me/s/1410152383611769410'
};

const normalizeSearchText = (value?: string) =>
    (value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

const drawWrappedText = (
    context: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number,
) => {
    const words = text.split(' ');
    let line = '';
    let currentY = y;

    words.forEach((word, index) => {
        const testLine = line ? `${line} ${word}` : word;
        const metrics = context.measureText(testLine);

        if (metrics.width > maxWidth && line) {
            context.fillText(line, x, currentY);
            line = word;
            currentY += lineHeight;
        } else {
            line = testLine;
        }

        if (index === words.length - 1 && line) {
            context.fillText(line, x, currentY);
        }
    });
};

const createColdBrewBannerFile = async (): Promise<File> => {
    const canvas = document.createElement('canvas');
    canvas.width = 720;
    canvas.height = 1080;

    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Không thể tạo banner trên trình duyệt này');
    }

    const background = context.createLinearGradient(0, 0, 720, 1080);
    background.addColorStop(0, '#071916');
    background.addColorStop(0.42, '#12392f');
    background.addColorStop(1, '#c58645');
    context.fillStyle = background;
    context.fillRect(0, 0, 720, 1080);

    context.fillStyle = 'rgba(255, 255, 255, 0.08)';
    for (let index = 0; index < 9; index += 1) {
        context.beginPath();
        context.arc(80 + index * 86, 160 + (index % 3) * 130, 58, 0, Math.PI * 2);
        context.fill();
    }

    context.save();
    context.translate(530, 252);
    context.rotate(-0.26);
    context.fillStyle = 'rgba(255, 246, 222, 0.92)';
    context.roundRect(-92, -150, 184, 300, 34);
    context.fill();
    context.fillStyle = 'rgba(76, 42, 24, 0.88)';
    context.roundRect(-66, -104, 132, 220, 24);
    context.fill();
    context.fillStyle = 'rgba(231, 247, 238, 0.72)';
    context.beginPath();
    context.ellipse(-22, -72, 22, 12, 0.4, 0, Math.PI * 2);
    context.ellipse(32, -18, 25, 14, -0.2, 0, Math.PI * 2);
    context.ellipse(-18, 50, 28, 15, 0.1, 0, Math.PI * 2);
    context.fill();
    context.restore();

    context.strokeStyle = 'rgba(255, 246, 222, 0.5)';
    context.lineWidth = 2;
    context.strokeRect(42, 42, 636, 996);

    context.fillStyle = '#fff3d2';
    context.font = '700 40px Georgia, serif';
    context.fillText('8AM COFFEE', 72, 118);

    context.fillStyle = '#ffe6a5';
    context.font = '700 78px Georgia, serif';
    drawWrappedText(context, 'Tặng Cold Brew', 72, 430, 540, 86);

    context.fillStyle = '#ffffff';
    context.font = '600 36px Georgia, serif';
    drawWrappedText(context, 'Mát lạnh, tỉnh táo, nhận quà tại cơ sở còn lượt', 72, 626, 560, 48);

    context.fillStyle = 'rgba(255, 246, 222, 0.94)';
    context.roundRect(72, 862, 430, 74, 37);
    context.fill();
    context.fillStyle = '#12392f';
    context.font = '700 30px Georgia, serif';
    context.fillText('Mở tin để nhận quà', 108, 909);

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Không thể xuất banner Cold Brew'));
                return;
            }

            resolve(new File([blob], 'cold-brew-gift-banner.png', { type: 'image/png' }));
        }, 'image/png');
    });
};

const formatFileSize = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(2)}MB`;

const normalizeToPlainText = (htmlContent?: string) => {
    if (!htmlContent) return '';
    return htmlContent
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&#39;/g, "'")
        .replace(/&quot;/g, '"')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

const loadImageFile = (file: File): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const imageUrl = URL.createObjectURL(file);
        const image = new Image();

        image.onload = () => {
            URL.revokeObjectURL(imageUrl);
            resolve(image);
        };

        image.onerror = () => {
            URL.revokeObjectURL(imageUrl);
            reject(new Error('Không thể đọc hình ảnh banner'));
        };

        image.src = imageUrl;
    });

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> =>
    new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
                return;
            }

            reject(new Error('Không thể nén hình ảnh banner'));
        }, type, quality);
    });

const buildCompressedFileName = (fileName: string) => {
    const lastDotIndex = fileName.lastIndexOf('.');
    const baseName = lastDotIndex > 0 ? fileName.slice(0, lastDotIndex) : fileName;
    return `${baseName}-zalo.jpg`;
};

const compressBannerImage = async (file: File): Promise<File> => {
    if (file.size <= BANNER_MAX_SIZE_BYTES) {
        return file;
    }

    const image = await loadImageFile(file);
    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;
    let scale = Math.min(1, BANNER_MAX_SIDE_PX / Math.max(sourceWidth, sourceHeight));
    let smallestBlob: Blob | null = null;

    for (let attempt = 0; attempt < 5; attempt += 1) {
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(sourceWidth * scale));
        canvas.height = Math.max(1, Math.round(sourceHeight * scale));

        const context = canvas.getContext('2d');
        if (!context) {
            throw new Error('Không thể nén banner trên trình duyệt này');
        }

        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        for (
            let quality = 0.9;
            quality >= BANNER_MIN_JPEG_QUALITY;
            quality -= BANNER_QUALITY_STEP
        ) {
            const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
            if (!smallestBlob || blob.size < smallestBlob.size) {
                smallestBlob = blob;
            }

            if (blob.size <= BANNER_MAX_SIZE_BYTES) {
                return new File([blob], buildCompressedFileName(file.name), {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                });
            }
        }

        scale *= 0.82;
    }

    throw new Error(
        `Không thể tự nén banner xuống dưới ${BANNER_MAX_SIZE_MB}MB. ` +
        `Ảnh hiện nhỏ nhất còn ${formatFileSize(smallestBlob?.size || file.size)}.`
    );
};

const MessageManagement: React.FC = () => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingMessage, setEditingMessage] = useState<Message | null>(null);
    const [form] = Form.useForm();
    const [filter] = useState<MessageFilter>({});
    const [bannerFile, setBannerFile] = useState<File | null>(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [sendModalVisible, setSendModalVisible] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
    const [sendForm] = Form.useForm();
    const [sendLoading, setSendLoading] = useState(false);
    const [availableProducts, setAvailableProducts] = useState<RelatedProduct[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [availableGifts, setAvailableGifts] = useState<Gift[]>([]);
    const [loadingGifts, setLoadingGifts] = useState(false);
    const [availableStores, setAvailableStores] = useState<Store[]>([]);
    const [loadingStores, setLoadingStores] = useState(false);

    const isMountedRef = useRef(true);
    const fetchMessagesRequestIdRef = useRef(0);
    const productsFetchedRef = useRef(false);
    const giftsFetchedRef = useRef(false);
    const productsRequestRef = useRef<Promise<RelatedProduct[]> | null>(null);
    const giftsRequestRef = useRef<Promise<Gift[]> | null>(null);
    const storesRequestRef = useRef<Promise<Store[]> | null>(null);
    const productsCacheRef = useRef<RelatedProduct[]>([]);
    const giftsCacheRef = useRef<Gift[]>([]);
    const storesCacheRef = useRef<Store[]>([]);
    const broadcastsCacheRef = useRef<Broadcast[] | null>(null);
    const bannerValidationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const selectedRelatedGiftIds = Form.useWatch('relatedGifts', form) as string[] | undefined;

    const selectableGifts = useMemo(() => {
        const giftsById = new Map<string, RelatedGiftMessageItem>();

        availableGifts.forEach((gift) => {
            giftsById.set(gift.id, gift);
        });

        editingMessage?.related_gifts?.forEach((gift) => {
            if (gift?.id && !giftsById.has(gift.id)) {
                giftsById.set(gift.id, {
                    ...gift,
                    isActive: gift.isActive !== false,
                });
            }
        });

        return Array.from(giftsById.values());
    }, [availableGifts, editingMessage]);

    const selectedRelatedGifts = useMemo(
        () => (selectedRelatedGiftIds || [])
            .map((giftId) => selectableGifts.find((gift) => gift.id === giftId))
            .filter(Boolean) as RelatedGiftMessageItem[],
        [selectableGifts, selectedRelatedGiftIds],
    );

    // Tour state and refs
    const [isTourOpen, setIsTourOpen] = useState(false);
    const [isModalTourOpen, setIsModalTourOpen] = useState(false);
    const titleRef = useRef(null);
    const addButtonRef = useRef(null);
    const tableContainerRef = useRef(null);

    // References for modal tour targets
    const modalTitleRef = useRef(null);
    const bannerFieldRef = useRef(null);
    const headerContentFieldRef = useRef(null);
    const textContentFieldRef = useRef(null);
    const relatedProductsFieldRef = useRef(null);
    const buttonsFieldRef = useRef(null);
    const submitButtonRef = useRef(null);

    const tourSteps: TourProps['steps'] = [
        {
            title: 'Quản lý tin nhắn',
            description: 'Chào mừng bạn đến với trang Quản lý tin nhắn! Đây là nơi bạn có thể tạo và quản lý các tin nhắn template để gửi đến khách hàng qua các kênh truyền thông.',
            target: () => titleRef.current,
        },
        {
            title: 'Thêm tin nhắn mới',
            description: 'Nhấn vào đây để tạo tin nhắn mới. Bạn có thể thiết kế tin nhắn với banner, tiêu đề, nội dung và các nút thao tác để tương tác với khách hàng.',
            target: () => addButtonRef.current,
        },
        {
            title: 'Danh sách tin nhắn',
            description: 'Bảng này hiển thị tất cả các tin nhắn đã tạo. Bạn có thể xem nội dung, chỉnh sửa, xóa hoặc gửi tin nhắn đến khách hàng. Mỗi tin nhắn có thể được sử dụng trong các chiến dịch broadcast.',
            target: () => tableContainerRef.current,
        },
        {
            title: 'Kết thúc hướng dẫn!',
            description: 'Cảm ơn bạn đã xem hướng dẫn này. Bây giờ bạn có thể tạo và quản lý tin nhắn hiệu quả để tương tác với khách hàng của mình.',
            target: () => titleRef.current,
        },
    ];

    const modalTourSteps: TourProps['steps'] = [
        {
            title: 'Form thêm tin nhắn',
            description: 'Đây là form để tạo tin nhắn template mới. Tin nhắn này có thể được sử dụng trong các chiến dịch broadcast để gửi đến khách hàng qua Zalo.',
            target: () => modalTitleRef.current,
        },
        {
            title: 'Banner tin nhắn',
            description: `Chọn hình ảnh banner cho tin nhắn. Banner sẽ hiển thị ở đầu tin nhắn và thu hút sự chú ý của khách hàng. Kích thước tối đa ${BANNER_MAX_SIZE_MB}MB, tỷ lệ khuyến nghị 1:1.5. Đây là thông tin bắt buộc.`,
            target: () => bannerFieldRef.current,
        },
        {
            title: 'Tiêu đề tin nhắn',
            description: 'Nhập tiêu đề hấp dẫn cho tin nhắn (VD: "🎉 Đã đạt thành viên Platinum 🎉", "☕ Ưu đãi đặc biệt hôm nay"). Tiêu đề tối đa 100 ký tự. Đây là thông tin bắt buộc.',
            target: () => headerContentFieldRef.current,
        },
        {
            title: 'Nội dung tin nhắn',
            description: 'Viết nội dung chi tiết cho tin nhắn. Mô tả thông tin, ưu đãi hoặc thông báo muốn truyền tải đến khách hàng. Nội dung tối đa 1000 ký tự. Đây là thông tin bắt buộc.',
            target: () => textContentFieldRef.current,
        },
        {
            title: 'Sản phẩm liên quan',
            description: 'Chọn các sản phẩm liên quan đến tin nhắn này như cà phê, đồ uống đóng chai, món ăn, máy pha hay máy xay. Điều này giúp bạn theo dõi tin nhắn có liên quan đến sản phẩm nào cho việc phát triển sau này. Đây là thông tin bắt buộc.',
            target: () => relatedProductsFieldRef.current,
        },
        {
            title: 'Nút thao tác',
            description: 'Thêm các nút để khách hàng có thể thực hiện hành động (VD: "Xem chi tiết", "Mua ngay", "Liên hệ"). Mỗi nút cần có tiêu đề (tối đa 20 ký tự) và URL đích. Có thể thêm tối đa 4 nút.',
            target: () => buttonsFieldRef.current,
        },
        {
            title: 'Hoàn thành',
            description: 'Kiểm tra lại tất cả thông tin và nhấn "Thêm mới" để lưu tin nhắn template vào hệ thống. Tin nhắn này có thể được sử dụng trong các chiến dịch broadcast sau này.',
            target: () => submitButtonRef.current,
        },
    ];

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
            if (bannerValidationTimeoutRef.current) {
                clearTimeout(bannerValidationTimeoutRef.current);
            }
        };
    }, []);

    const fetchMessages = useCallback(async () => {
        const requestId = ++fetchMessagesRequestIdRef.current;
        setLoading(true);
        try {
            const data = await messageService.getAllMessages();

            if (isMountedRef.current && requestId === fetchMessagesRequestIdRef.current) {
                setMessages(data);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
            if (isMountedRef.current) {
                message.error('Không thể tải danh sách tin nhắn');
            }
        } finally {
            if (isMountedRef.current && requestId === fetchMessagesRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        void fetchMessages();
    }, [fetchMessages]);

    const loadProducts = useCallback(async (): Promise<RelatedProduct[]> => {
        if (productsFetchedRef.current) {
            return productsCacheRef.current;
        }

        if (productsRequestRef.current) {
            return productsRequestRef.current;
        }

        setLoadingProducts(true);
        const request = relatedProductService.getAllProducts();
        productsRequestRef.current = request;

        try {
            const products = await request;
            if (isMountedRef.current) {
                setAvailableProducts(products);
            }
            productsCacheRef.current = products;
            productsFetchedRef.current = true;
            return products;
        } catch (error) {
            console.error('Error loading products:', error);
            if (isMountedRef.current) {
                message.error('Không thể tải danh sách sản phẩm');
            }
            throw error;
        } finally {
            productsRequestRef.current = null;
            if (isMountedRef.current) {
                setLoadingProducts(false);
            }
        }
    }, []);

    const loadGifts = useCallback(async (): Promise<Gift[]> => {
        if (giftsFetchedRef.current) {
            return giftsCacheRef.current;
        }

        if (giftsRequestRef.current) {
            return giftsRequestRef.current;
        }

        setLoadingGifts(true);
        const request = giftService.getGifts();
        giftsRequestRef.current = request;

        try {
            const gifts = await request;
            const activeGifts = gifts.filter((gift) => isGiftActive(gift));
            if (isMountedRef.current) {
                setAvailableGifts(activeGifts);
            }
            giftsCacheRef.current = activeGifts;
            giftsFetchedRef.current = true;
            return activeGifts;
        } catch (error) {
            console.error('Error loading gifts:', error);
            if (isMountedRef.current) {
                message.error('Không thể tải danh sách quà tặng');
            }
            throw error;
        } finally {
            giftsRequestRef.current = null;
            if (isMountedRef.current) {
                setLoadingGifts(false);
            }
        }
    }, []);

    const loadStores = useCallback(async (): Promise<Store[]> => {
        if (storesCacheRef.current.length > 0) {
            return storesCacheRef.current;
        }

        if (storesRequestRef.current) {
            return storesRequestRef.current;
        }

        setLoadingStores(true);
        const request = storeService.getActiveStores();
        storesRequestRef.current = request;

        try {
            const stores = await request;
            if (isMountedRef.current) {
                setAvailableStores(stores);
            }
            storesCacheRef.current = stores;
            return stores;
        } catch (error) {
            console.error('Error loading stores:', error);
            if (isMountedRef.current) {
                message.error('Không thể tải danh sách cơ sở');
            }
            throw error;
        } finally {
            storesRequestRef.current = null;
            if (isMountedRef.current) {
                setLoadingStores(false);
            }
        }
    }, []);

    // Chỉ tải products và gifts khi mở modal thêm/sửa (không cần khi chỉ xem danh sách)
    useEffect(() => {
        if (modalVisible) {
            void loadProducts();
            void loadGifts();
            void loadStores();
        }
    }, [modalVisible, loadGifts, loadProducts, loadStores]);

    const getGiftStoreOptions = useCallback((gift: RelatedGiftMessageItem) => {
        const storeMap = new Map<string, { label: string; disabled?: boolean }>();

        gift.storeAllocations
            ?.filter((allocation) => allocation.totalQuantity > 0)
            .forEach((allocation) => {
                storeMap.set(allocation.storeId, {
                    label: `${allocation.storeName} - còn ${allocation.availableQuantity}/${allocation.totalQuantity}`,
                    disabled: allocation.availableQuantity <= 0 && gift.storeId !== allocation.storeId,
                });
            });

        if (gift.storeId) {
            const existingStore = availableStores.find((store) => store.id === gift.storeId);
            storeMap.set(gift.storeId, {
                label: gift.storeName || existingStore?.name || gift.storeId,
                disabled: false,
            });
        }

        return Array.from(storeMap.entries()).map(([value, option]) => ({
            value,
            label: option.label,
            disabled: option.disabled,
        }));
    }, [availableStores]);

    const handleSubmit = async (values: any) => {
        try {
            console.log('values: ', values);

            setSubmitLoading(true);

            // Kiểm tra kích thước file trước khi submit
            if (bannerFile && bannerFile.size > BANNER_MAX_SIZE_BYTES) {
                message.error(`Kích thước hình ảnh (${formatFileSize(bannerFile.size)}) vượt quá giới hạn ${BANNER_MAX_SIZE_MB}MB!`);
                setSubmitLoading(false);
                return;
            }

            if (!bannerFile && !editingMessage?.template_data?.banner?.image_url) {
                message.error('Vui lòng chọn banner cho tin nhắn');
                setSubmitLoading(false);
                return;
            }

            const existingBanner = editingMessage?.template_data?.banner;
            if (
                !bannerFile &&
                existingBanner?.image_url &&
                existingBanner.max_size &&
                existingBanner.max_size !== `${BANNER_MAX_SIZE_MB}MB`
            ) {
                message.error(`Banner hiện tại đang lưu theo giới hạn ${existingBanner.max_size}. Vui lòng tải lại ảnh để hệ thống nén xuống ${BANNER_MAX_SIZE_MB}MB trước khi gửi broadcast.`);
                setSubmitLoading(false);
                return;
            }

            const headerContent = values.headerContent?.trim();
            const textContent = values.textContent?.trim().replace(/\n/g, '<br>');

            console.log('headerContent: ', headerContent);
            console.log('textContent: ', textContent);

            if (headerContent && headerContent.length > 100) {
                message.error('Tiêu đề không được vượt quá 100 ký tự');
                setSubmitLoading(false);
                return;
            }

            if (textContent && textContent.length > 1000) {
                message.error('Nội dung không được vượt quá 1000 ký tự');
                setSubmitLoading(false);
                return;
            }

            if ((values.relatedProducts || []).length === 0 && (values.relatedGifts || []).length === 0) {
                message.error('Vui lòng chọn ít nhất một sản phẩm hoặc quà tặng liên quan');
                setSubmitLoading(false);
                return;
            }

            const relatedGiftStoreMap = values.relatedGiftStores || {};
            const relatedGifts = (values.relatedGifts || []).flatMap((giftId: string) => {
                const gift = selectableGifts.find((g) => g.id === giftId);
                if (!gift) {
                    return [];
                }

                const selectedStoreIds = Array.isArray(relatedGiftStoreMap[giftId])
                    ? relatedGiftStoreMap[giftId]
                    : relatedGiftStoreMap[giftId]
                        ? [relatedGiftStoreMap[giftId]]
                        : [];

                if (selectedStoreIds.length === 0) {
                    throw new Error(`Vui lòng chọn ít nhất một cơ sở nhận cho quà "${gift.name}"`);
                }

                return selectedStoreIds.map((selectedStoreId: string) => {
                    const matchedStoreAllocation = gift.storeAllocations?.find((allocation) => allocation.storeId === selectedStoreId);
                    const matchedStore = availableStores.find((store) => store.id === selectedStoreId);

                    return {
                        ...gift,
                        storeId: selectedStoreId,
                        storeName: matchedStoreAllocation?.storeName || matchedStore?.name || gift.storeName || selectedStoreId,
                    } as RelatedGiftMessageItem;
                });
            }) as RelatedGiftMessageItem[];

            const messageData: Partial<Message> = {
                type: 'template',
                template_data: {
                    header: {
                        content: headerContent,
                        align: values.headerAlign || 'center'
                    },
                    table: { rows: [] },
                    text: {
                        content: textContent || '',
                        align: 'left'
                    },
                    buttons: values.buttons?.filter((button: any) => button.title && button.url).map((button: any) => ({
                        title: button.title,
                        type: 'oa.open.url',
                        payload: {
                            url: button.url
                        }
                    })) || []
                },
                // Thêm sản phẩm liên quan
                related_products: values.relatedProducts?.map((productId: string) => {
                    const product = availableProducts.find(p => p.id === productId);
                    return product;
                }).filter(Boolean) || [],
                // Thêm quà tặng liên quan
                related_gifts: relatedGifts
            };

            console.log('messageData: ', messageData);

            if (editingMessage) {
                if (!bannerFile) {
                    messageData.template_data!.banner = editingMessage.template_data?.banner;
                }
                await messageService.updateMessage(editingMessage.id, messageData, bannerFile || undefined);
                message.success('Cập nhật tin nhắn thành công');
            } else {
                // Tạo tin nhắn mới
                const createdMessage = await messageService.createMessage(messageData, bannerFile!);
                const messageId = createdMessage.id;

                // Tự động thêm nút "Xem tin nhắn" vào danh sách buttons
                const updatedButtons = [
                    ...(createdMessage.template_data?.buttons || []),
                    {
                        title: "Xem tin nhắn",
                        type: "oa.open.url",
                        payload: {
                            url: `https://zalo.me/s/1410152383611769410/news/${messageId}`
                        }
                    }
                ];

                // Cập nhật lại message với buttons đã thêm nút mới
                await messageService.updateMessage(messageId, {
                    ...createdMessage,
                    template_data: {
                        ...createdMessage.template_data,
                        buttons: updatedButtons
                    }
                }, undefined);

                message.success('Tạo tin nhắn mới thành công');
            }
            setModalVisible(false);
            form.resetFields();
            setBannerFile(null);
            setFileList([]);
            void fetchMessages();
        } catch (error) {
            console.error('Error saving message:', error);
            message.error('Có lỗi xảy ra khi lưu tin nhắn');
        } finally {
            setSubmitLoading(false);
        }
    };

    const getBroadcastsForDelete = useCallback(async (): Promise<Broadcast[]> => {
        if (broadcastsCacheRef.current) {
            return broadcastsCacheRef.current;
        }

        const broadcasts = await broadcastService.getAllBroadcasts();
        broadcastsCacheRef.current = broadcasts;
        return broadcasts;
    }, []);

    const handleDelete = async (id: string) => {
        Modal.confirm({
            title: 'Xác nhận xóa',
            content: 'Khi xóa tin nhắn này, tất cả broadcast liên quan đến tin nhắn cũng sẽ bị xóa. Bạn có chắc chắn muốn xóa không?',
            okText: 'Xóa',
            okType: 'danger',
            cancelText: 'Hủy',
            onOk: async () => {
                try {
                    const broadcasts = await getBroadcastsForDelete();
                    const relatedBroadcasts = broadcasts.filter(b => b.messageId === id);

                    await Promise.all(
                        relatedBroadcasts
                            .filter((broadcast) => Boolean(broadcast.id))
                            .map((broadcast) => broadcastService.deleteBroadcast(broadcast.id!))
                    );

                    await messageService.deleteMessage(id);

                    if (broadcastsCacheRef.current) {
                        broadcastsCacheRef.current = broadcastsCacheRef.current.filter((broadcast) => broadcast.messageId !== id);
                    }

                    message.success('Xóa tin nhắn và các broadcast liên quan thành công');
                    void fetchMessages();
                } catch (error) {
                    console.error('Error deleting message:', error);
                    message.error('Không thể xóa tin nhắn');
                }
            }
        });
    };

    const validateBannerField = useCallback(() => {
        if (bannerValidationTimeoutRef.current) {
            clearTimeout(bannerValidationTimeoutRef.current);
        }

        bannerValidationTimeoutRef.current = setTimeout(() => {
            if (!isMountedRef.current) {
                return;
            }
            form.validateFields(['banner']).catch(() => { });
        }, 0);
    }, [form]);

    const applyColdBrewPreset = useCallback(async () => {
        setEditingMessage(null);
        form.resetFields();
        setBannerFile(null);
        setFileList([]);
        setModalVisible(true);

        try {
            const [products, gifts, stores, generatedBanner] = await Promise.all([
                loadProducts().catch(() => [] as RelatedProduct[]),
                loadGifts().catch(() => [] as Gift[]),
                loadStores().catch(() => [] as Store[]),
                createColdBrewBannerFile(),
            ]);
            const banner = await compressBannerImage(generatedBanner);

            const coldBrewMatcher = (name?: string) => {
                const normalizedName = normalizeSearchText(name);
                return normalizedName.includes('cold brew') || normalizedName.includes('coldbrew');
            };
            const coldBrewProducts = products.filter((product) => coldBrewMatcher(product.name));
            const coldBrewGifts = gifts.filter((gift) => coldBrewMatcher(gift.name));
            const relatedGiftStores = coldBrewGifts.reduce((acc, gift) => {
                const selectableStoreIds = gift.storeAllocations
                    ?.filter((allocation) => allocation.totalQuantity > 0 && allocation.availableQuantity > 0)
                    .map((allocation) => allocation.storeId) || [];
                const fallbackStoreIds = gift.storeAllocations
                    ?.filter((allocation) => allocation.totalQuantity > 0)
                    .map((allocation) => allocation.storeId) || [];
                const fallbackStore = stores[0];
                const selectedStoreIds = selectableStoreIds.length > 0 ? selectableStoreIds : fallbackStoreIds;

                if (selectedStoreIds.length > 0) {
                    acc[gift.id] = selectedStoreIds;
                } else if (fallbackStore?.id) {
                    acc[gift.id] = [fallbackStore.id];
                }

                return acc;
            }, {} as Record<string, string[]>);

            setBannerFile(banner);
            setFileList([{
                uid: 'cold-brew-preset-banner',
                name: banner.name,
                status: 'done',
                url: URL.createObjectURL(banner),
            }]);

            form.setFieldsValue({
                banner,
                headerContent: COLD_BREW_PRESET.header,
                textContent: COLD_BREW_PRESET.text,
                buttons: [{
                    title: 'Nhận quà',
                    url: COLD_BREW_PRESET.actionUrl,
                }],
                relatedProducts: coldBrewProducts.map((product) => product.id),
                relatedGifts: coldBrewGifts.map((gift) => gift.id),
                relatedGiftStores,
            });
            validateBannerField();

            if (coldBrewGifts.length === 0) {
                message.info('Đã tạo banner và nội dung. Chưa tìm thấy quà Cold Brew, hãy chọn quà thủ công trong form.');
            } else {
                message.success('Đã tạo sẵn tin quà Cold Brew. Kiểm tra cơ sở nhận quà trước khi lưu.');
            }
        } catch (error) {
            console.error('Error applying cold brew preset:', error);
            message.error('Không thể tạo preset tin quà Cold Brew');
        }
    }, [form, loadGifts, loadProducts, loadStores, validateBannerField]);

    const handleImagesChange = async ({ fileList: newFileList }: any) => {
        if (newFileList.length === 0) {
            setBannerFile(null);
            setFileList([]);
            form.setFieldValue('banner', null);
            validateBannerField();
            return;
        }

        const uploadFile = newFileList[0];
        const file = uploadFile.originFileObj || uploadFile;

        if (file instanceof File) {
            if (!file.type.startsWith('image/')) {
                message.error('Chỉ cho phép upload file hình ảnh (JPG, PNG)!');
                return;
            }

            try {
                form.setFieldValue('banner', 'processing');
                setFileList([{ ...uploadFile, status: 'uploading' }]);

                const preparedFile = await compressBannerImage(file);
                const previewUrl = URL.createObjectURL(preparedFile);
                const nextFileList: UploadFile[] = [{
                    uid: uploadFile.uid || `${Date.now()}`,
                    name: preparedFile.name,
                    status: 'done',
                    size: preparedFile.size,
                    type: preparedFile.type,
                    originFileObj: preparedFile as any,
                    url: previewUrl
                }];

                if (preparedFile.size !== file.size || preparedFile.name !== file.name) {
                    message.info(`Đã nén banner từ ${formatFileSize(file.size)} xuống ${formatFileSize(preparedFile.size)} để phù hợp giới hạn Zalo.`);
                }

                setBannerFile(preparedFile);
                setFileList(nextFileList);
                form.setFieldValue('banner', preparedFile);
                validateBannerField();
            } catch (error: any) {
                message.error(error?.message || 'Không thể xử lý banner');
                setBannerFile(null);
                setFileList([]);
                form.setFieldValue('banner', null);
                validateBannerField();
            }
            return;
        }

        setFileList(newFileList);
        if (file?.url) {
            form.setFieldValue('banner', 'uploaded');
            validateBannerField();
        } else {
            form.setFieldValue('banner', 'processing');
        }
    };

    const handleEdit = (record: Message) => {
        setEditingMessage(record);
        form.setFieldsValue({
            headerContent: record.template_data?.header?.content,
            textContent: record.template_data?.text?.content?.replace(/<br>/g, '\n'),
            buttons: record.template_data?.buttons?.map(button => ({
                title: button.title,
                url: button.payload?.url
            })) || [],
            relatedProducts: record.related_products?.map(product => product.id) || [],
            relatedGifts: Array.from(new Set(record.related_gifts?.map(gift => gift.id) || [])),
            relatedGiftStores: (record.related_gifts || []).reduce((acc, gift) => {
                if (gift.id && gift.storeId) {
                    acc[gift.id] = [...(acc[gift.id] || []), gift.storeId];
                }
                return acc;
            }, {} as Record<string, string[]>)
        });

        // Set banner image if exists
        if (record.template_data?.banner?.image_url) {
            setFileList([{
                uid: '-1',
                name: 'banner.png',
                status: 'done',
                url: record.template_data.banner.image_url
            }]);
            // Set form field để validation hoạt động đúng
            form.setFieldValue('banner', 'existing');
        } else {
            setFileList([]);
            form.setFieldValue('banner', null);
        }
        setModalVisible(true);
    };

    const handleSendMessage = async (values: any) => {
        try {
            setSendLoading(true);
            if (!selectedMessage) return;

            const config = await configService.getConfig();
            if (
                selectedMessage.template_data?.banner?.image_url &&
                selectedMessage.template_data.banner.max_size &&
                selectedMessage.template_data.banner.max_size !== `${BANNER_MAX_SIZE_MB}MB`
            ) {
                throw new Error(`Banner của tin này đang vượt giới hạn ${BANNER_MAX_SIZE_MB}MB của Zalo. Vui lòng sửa tin và tải lại ảnh; hệ thống sẽ tự nén ảnh trước khi lưu.`);
            }

            const assertZaloSuccess = (response: any) => {
                if (response.data && typeof response.data.error !== 'undefined' && response.data.error !== 0) {
                    throw new Error(response.data.message || response.data.error_message || `Zalo từ chối gửi tin (error ${response.data.error})`);
                }
            };

            const headers = {
                'access_token': config?.access_token_zalo,
                'Content-Type': 'application/json'
            };
            const bannerImageUrl = selectedMessage.template_data?.banner?.image_url?.trim();
            const actionUrl = selectedMessage.template_data?.buttons?.find((button: any) => button?.payload?.url)?.payload?.url;
            const textSections = [
                selectedMessage.template_data?.header?.content,
                selectedMessage.template_data?.table?.rows?.map((row) => `${row.key}: ${row.value}`).join('\n'),
                selectedMessage.template_data?.text?.content,
                selectedMessage.content,
                actionUrl ? `Xem tin nhắn: ${actionUrl}` : ''
            ]
                .map((section) => normalizeToPlainText(section))
                .filter(Boolean);
            const combinedText = textSections.join('\n\n').trim();

            if (!combinedText && !bannerImageUrl) {
                throw new Error('Tin nhắn đã chọn không có nội dung để gửi');
            }

            if (bannerImageUrl) {
                const imageResponse = await axios.post('https://openapi.zalo.me/v3.0/oa/message/cs', {
                    recipient: {
                        user_id: values.user_id
                    },
                    message: {
                        attachment: {
                            type: 'template',
                            payload: {
                                template_type: 'media',
                                elements: [
                                    {
                                        media_type: 'image',
                                        url: bannerImageUrl
                                    }
                                ]
                            }
                        }
                    }
                }, { headers });

                assertZaloSuccess(imageResponse);
            }

            if (combinedText) {
                const textResponse = await axios.post('https://openapi.zalo.me/v3.0/oa/message/cs', {
                    recipient: {
                        user_id: values.user_id
                    },
                    message: {
                        text: combinedText
                    }
                }, { headers });

                assertZaloSuccess(textResponse);
            }

            message.success('Gửi tin nhắn thành công');
            setSendModalVisible(false);
            sendForm.resetFields();
        } catch (error: any) {
            console.error('Error sending message:', error);
            message.error(error?.message || 'Có lỗi xảy ra khi gửi tin nhắn');
        } finally {
            setSendLoading(false);
        }
    };

    const columns = [
        {
            title: 'Nội dung',
            dataIndex: 'content',
            key: 'content',
            render: (_: string, record: Message) => {
                if (record.type === 'template') {
                    return (
                        <div>
                            {record.template_data?.banner && (
                                <div>
                                    <strong>Banner:</strong>
                                    <div style={{ marginTop: 8, marginBottom: 8 }}>
                                        <img
                                            src={record.template_data.banner.image_url}
                                            alt="Banner"
                                            style={{
                                                width: 100,
                                                height: 150,
                                                objectFit: 'cover',
                                                borderRadius: 4
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                            {record.template_data?.header?.content && (
                                <div><strong>Tiêu đề:</strong> {record.template_data.header.content}</div>
                            )}
                            {record.template_data?.text?.content && (
                                <div>
                                    <strong>Nội dung:</strong>
                                    <div dangerouslySetInnerHTML={{ __html: record.template_data.text.content }} />
                                </div>
                            )}
                            {record.template_data?.buttons && record.template_data.buttons.length > 0 && (
                                <div>
                                    <strong>Nút:</strong>
                                    <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {record.template_data.buttons.map((button, index) => (
                                            <Tag key={index} color="blue">
                                                {button.title} - {button.payload?.url}
                                            </Tag>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {record.related_products && record.related_products.length > 0 && (
                                <div style={{ marginTop: 8 }}>
                                    <strong>Sản phẩm liên quan:</strong>
                                    <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {record.related_products.map((product, index) => {
                                            const getProductColor = (type: string) => {
                                                switch (type) {
                                                    case 'coffee': return 'brown';
                                                    case 'bottled_drink': return 'blue';
                                                    case 'dish': return 'green';
                                                    case 'brewer': return 'orange';
                                                    case 'grinder': return 'purple';
                                                    default: return 'default';
                                                }
                                            };
                                            return (
                                                <Tag key={index} color={getProductColor(product.type)}>
                                                    {product.name} {product.price && `- ${product.price.toLocaleString()}đ`}
                                                </Tag>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            {record.related_gifts && record.related_gifts.length > 0 && (
                                <div style={{ marginTop: 8 }}>
                                    <strong>Quà tặng liên quan:</strong>
                                    <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                        {record.related_gifts.map((gift, index) => (
                                            <Tag key={index} color="orange">
                                                {gift.name}
                                                {gift.storeName ? ` - nhận tại ${gift.storeName}` : ''}
                                                {typeof gift.availableQuantity === 'number' ? ` - còn ${gift.availableQuantity}` : ''}
                                            </Tag>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                }
                return record.content;
            }
        },
        {
            title: 'Thời gian tạo',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (timestamp: any) => {
                if (!timestamp) return '';
                // Kiểm tra nếu timestamp là Firestore Timestamp
                if (timestamp.seconds) {
                    return moment(timestamp.seconds * 1000).format('DD/MM/YYYY HH:mm:ss');
                }
                // Nếu là Date object hoặc string
                return moment(timestamp).format('DD/MM/YYYY HH:mm:ss');
            }
        },
        // {
        //     title: 'Trạng thái',
        //     dataIndex: 'status',
        //     key: 'status',
        //     render: (status: string) => {
        //         const color = status === 'read' ? 'green' : status === 'delivered' ? 'blue' : 'gold';
        //         const text = status === 'read' ? 'Đã đọc' : status === 'delivered' ? 'Đã gửi' : 'Chờ gửi';
        //         return <Tag color={color}>{text}</Tag>;
        //     }
        // },
        {
            title: 'Thao tác',
            key: 'action',
            render: (_: any, record: Message) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record.id)}
                    />
                </Space>
            ),
        },
    ];



    const filteredMessages = useMemo(() => {
        let filtered = [...messages];

        if (filter.searchText) {
            filtered = filtered.filter(msg =>
                msg.template_data?.header?.content?.toLowerCase().includes(filter.searchText!.toLowerCase()) ||
                msg.template_data?.text?.content?.toLowerCase().includes(filter.searchText!.toLowerCase()) ||
                msg.template_data?.table?.rows?.some(row =>
                    row.key.toLowerCase().includes(filter.searchText!.toLowerCase()) ||
                    row.value.toLowerCase().includes(filter.searchText!.toLowerCase())
                )
            );
        }

        if (filter.status) {
            filtered = filtered.filter(msg => msg.status === filter.status);
        }

        if (filter.startDate && filter.endDate) {
            filtered = filtered.filter(msg => {
                const msgDate = new Date(msg.timestamp);
                return msgDate >= filter.startDate! && msgDate <= filter.endDate!;
            });
        }

        return filtered;
    }, [messages, filter]);

    return (
        <AdminLayout>
            <div className="admin-page-header">
                <div className="admin-page-title">
                    <h1 ref={titleRef}>Quản lý tin nhắn</h1>
                    <Tag color="blue">{messages.length} tin nhắn</Tag>
                    <Button
                        type="primary"
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setIsTourOpen(true)}
                        style={{ marginLeft: 8 }}
                    >
                        Hướng dẫn
                    </Button>
                </div>

                <div className="admin-page-actions">
                    <div className="admin-search-filters">
                        <Button
                            icon={<GiftOutlined />}
                            onClick={applyColdBrewPreset}
                            style={{
                                height: 40,
                                borderColor: '#c58645',
                                color: '#7a431d',
                                background: 'linear-gradient(135deg, #fff8e8 0%, #f3dfbc 100%)',
                            }}
                        >
                            Tạo tin quà Cold Brew
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setEditingMessage(null);
                                form.resetFields();
                                setModalVisible(true);
                            }}
                            className="admin-button"
                            ref={addButtonRef}
                        >
                            Thêm tin nhắn mới
                        </Button>
                    </div>
                </div>
            </div>

            <div className="admin-form-section">
                <div className="admin-table-container" ref={tableContainerRef}>
                    <Table
                        className="admin-table"
                        columns={columns}
                        dataSource={filteredMessages}
                        loading={loading}
                        rowKey="id"
                        scroll={{ x: 1500 }}
                        pagination={{
                            defaultPageSize: 10,
                            position: ['bottomRight']
                        }}
                    />
                </div>
            </div>

            <Modal
                title={editingMessage ? "Chỉnh sửa tin nhắn" : "Thêm tin nhắn mới"}
                visible={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    setIsModalTourOpen(false); // Reset modal tour state
                    form.resetFields();
                    setEditingMessage(null);
                    setBannerFile(null);
                    setFileList([]);
                }}
                footer={null}
                width={800}
                className="admin-modal"
                destroyOnClose={true}
            >
                <div className="admin-form-container">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <h3 ref={modalTitleRef} style={{ margin: 0 }}>
                            {editingMessage ? "Cập nhật tin nhắn template" : "Thêm tin nhắn template mới"}
                        </h3>
                        <Button
                            type="primary"
                            icon={<QuestionCircleOutlined />}
                            onClick={() => {
                                setIsModalTourOpen(true);
                            }}
                            size="small"
                        >
                            Hướng dẫn điền form
                        </Button>
                    </div>

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                    >
                        {!editingMessage && (
                            <div
                                style={{
                                    padding: 18,
                                    marginBottom: 20,
                                    borderRadius: 18,
                                    background: 'radial-gradient(circle at 88% 14%, rgba(255,246,222,0.28), transparent 26%), linear-gradient(135deg, #08211d 0%, #173f34 48%, #b8793e 100%)',
                                    color: '#fff8e8',
                                    boxShadow: '0 18px 42px rgba(9, 34, 29, 0.22)',
                                    overflow: 'hidden',
                                    position: 'relative',
                                }}
                            >
                                <div style={{ maxWidth: 520 }}>
                                    <Tag color="gold" style={{ marginBottom: 10 }}>Preset nhanh</Tag>
                                    <h3 style={{ margin: '0 0 8px', color: '#fff8e8', fontSize: 22 }}>
                                        Banner tin quà Cold Brew
                                    </h3>
                                    <p style={{ margin: '0 0 14px', color: 'rgba(255, 248, 232, 0.86)' }}>
                                        Tự tạo banner PNG, điền sẵn nội dung và chọn quà Cold Brew nếu đã có trong danh sách quà tặng.
                                    </p>
                                    <Button
                                        icon={<GiftOutlined />}
                                        onClick={applyColdBrewPreset}
                                        style={{ border: 0, background: '#fff2cc', color: '#12392f', fontWeight: 700 }}
                                    >
                                        Dùng banner Cold Brew
                                    </Button>
                                </div>
                                <div
                                    style={{
                                        position: 'absolute',
                                        right: 28,
                                        bottom: -18,
                                        width: 110,
                                        height: 190,
                                        borderRadius: '36px 36px 20px 20px',
                                        background: 'linear-gradient(180deg, #fff6de 0%, #6a3c22 28%, #2a1810 100%)',
                                        boxShadow: '0 16px 34px rgba(0,0,0,0.28)',
                                        transform: 'rotate(-10deg)',
                                    }}
                                />
                            </div>
                        )}

                        <div className="admin-form-section" ref={bannerFieldRef}>
                            <h3 className="admin-form-section-title">Banner</h3>
                            <Form.Item
                                name="banner"
                                label={<>Banner </>}
                                rules={[
                                    {
                                        required: true,
                                        message: 'Vui lòng chọn banner cho tin nhắn',
                                        validator: async (_, value) => {
                                            // Kiểm tra form field value hoặc state hoặc banner đã tồn tại khi edit
                                            const hasBannerFile = bannerFile !== null;
                                            const hasValidFormValue = value !== null && value !== undefined && value !== '' &&
                                                (value === 'uploaded' || value === 'processing' || value === 'existing' || value instanceof File);
                                            const hasExistingBanner = editingMessage?.template_data?.banner?.image_url;

                                            if (!hasBannerFile && !hasValidFormValue && !hasExistingBanner) {
                                                throw new Error('Vui lòng chọn banner cho tin nhắn');
                                            }
                                        }
                                    }
                                ]}
                                extra={`Hỗ trợ định dạng: JPG, PNG. Kích thước tối đa ${BANNER_MAX_SIZE_MB}MB. Ảnh lớn hơn sẽ được tự nén trước khi lưu. Tỉ lệ 1:1.5`}
                            >
                                <div className="admin-image-upload-section">
                                    <Upload
                                        listType="picture-card"
                                        fileList={fileList}
                                        onChange={handleImagesChange}
                                        beforeUpload={(file) => {
                                            const isImage = file.type.startsWith('image/');

                                            if (!isImage) {
                                                message.error('Chỉ cho phép upload file hình ảnh (JPG, PNG)!');
                                                return false;
                                            }

                                            return false;
                                        }}
                                        accept="image/jpeg,image/png"
                                        maxCount={1}
                                    >
                                        {fileList.length >= 1 ? null : (
                                            <div>
                                                <PlusOutlined />
                                                <div style={{ marginTop: 8 }}>Tải lên</div>
                                            </div>
                                        )}
                                    </Upload>
                                </div>
                            </Form.Item>
                        </div>

                        <div className="admin-form-section">
                            <h3 className="admin-form-section-title">Nội dung</h3>
                            <div ref={headerContentFieldRef}>
                                <Form.Item
                                    name="headerContent"
                                    label={<>Tiêu đề <span style={{ color: '#8c8c8c' }}>(Tối đa 100 ký tự)</span></>}
                                    rules={[
                                        { required: true, message: 'Vui lòng nhập tiêu đề' },
                                        { max: 100, message: 'Tiêu đề không được vượt quá 100 ký tự' }
                                    ]}
                                >
                                    <Input
                                        placeholder="Ví dụ: 🎉 Đã đạt thành viên Platinum 🎉"
                                        maxLength={100}
                                        showCount
                                    />
                                </Form.Item>
                            </div>

                            <div ref={textContentFieldRef}>
                                <Form.Item
                                    name="textContent"
                                    label={<>Nội dung <span style={{ color: '#8c8c8c' }}>(Tối đa 1000 ký tự)</span></>}
                                    rules={[
                                        { required: true, message: 'Vui lòng nhập nội dung' },
                                        { max: 1000, message: 'Nội dung không được vượt quá 1000 ký tự' }
                                    ]}
                                >
                                    <TextArea
                                        placeholder="Nhập nội dung tin nhắn"
                                        autoSize={{ minRows: 4, maxRows: 8 }}
                                        maxLength={1000}
                                        showCount
                                    />
                                </Form.Item>
                            </div>
                        </div>

                        <div className="admin-form-section" ref={relatedProductsFieldRef}>
                            <h3 className="admin-form-section-title">Sản phẩm liên quan</h3>
                            <Form.Item
                                name="relatedProducts"
                                label="Chọn sản phẩm liên quan"
                                extra="Có thể bỏ trống nếu tin nhắn chỉ dùng để gắn quà tặng"
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Chọn sản phẩm..."
                                    loading={loadingProducts}
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
                                    }
                                    style={{ width: '100%' }}
                                >
                                    <Select.OptGroup label="☕ Cà phê">
                                        {availableProducts
                                            .filter(product => product.type === 'coffee')
                                            .map(product => (
                                                <Option key={product.id} value={product.id}>
                                                    {product.name} {product.price && `- ${product.price.toLocaleString()}đ`}
                                                </Option>
                                            ))
                                        }
                                    </Select.OptGroup>
                                    <Select.OptGroup label="🧃 Đồ uống đóng chai">
                                        {availableProducts
                                            .filter(product => product.type === 'bottled_drink')
                                            .map(product => (
                                                <Option key={product.id} value={product.id}>
                                                    {product.name} {product.price && `- ${product.price.toLocaleString()}đ`}
                                                </Option>
                                            ))
                                        }
                                    </Select.OptGroup>
                                    <Select.OptGroup label="🍽️ Món ăn">
                                        {availableProducts
                                            .filter(product => product.type === 'dish')
                                            .map(product => (
                                                <Option key={product.id} value={product.id}>
                                                    {product.name} {product.price && `- ${product.price.toLocaleString()}đ`}
                                                </Option>
                                            ))
                                        }
                                    </Select.OptGroup>
                                    <Select.OptGroup label="☕ Máy pha">
                                        {availableProducts
                                            .filter(product => product.type === 'brewer')
                                            .map(product => (
                                                <Option key={product.id} value={product.id}>
                                                    {product.name} {product.price && `- ${product.price.toLocaleString()}đ`}
                                                </Option>
                                            ))
                                        }
                                    </Select.OptGroup>
                                    <Select.OptGroup label="⚙️ Máy xay">
                                        {availableProducts
                                            .filter(product => product.type === 'grinder')
                                            .map(product => (
                                                <Option key={product.id} value={product.id}>
                                                    {product.name} {product.price && `- ${product.price.toLocaleString()}đ`}
                                                </Option>
                                            ))
                                        }
                                    </Select.OptGroup>
                                </Select>
                            </Form.Item>
                        </div>

                        {/* INSERTED: Quà tặng liên quan */}
                        <div className="admin-form-section">
                            <h3 className="admin-form-section-title">Quà tặng liên quan</h3>
                            <Form.Item
                                name="relatedGifts"
                                label="Chọn quà tặng liên quan"
                                extra="Chọn các quà tặng sẽ được gợi ý/gắn với tin nhắn này"
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Chọn quà tặng..."
                                    loading={loadingGifts}
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
                                    }
                                    style={{ width: '100%' }}
                                >
                                    {selectableGifts.map(gift => (
                                        <Option key={gift.id} value={gift.id} disabled={!isGiftActive(gift)}>
                                            {gift.name}
                                            {!isGiftActive(gift) ? ' - đã ẩn' : ''}
                                            {typeof gift.availableQuantity === 'number' ? ` - còn ${gift.availableQuantity}` : ''}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            {selectedRelatedGifts.length > 0 && (
                                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                    {selectedRelatedGifts.map((gift) => {
                                        const storeOptions = getGiftStoreOptions(gift);
                                        return (
                                            <div
                                                key={gift.id}
                                                style={{
                                                    padding: 12,
                                                    border: '1px solid #f0f0f0',
                                                    borderRadius: 8,
                                                    background: '#fafafa',
                                                }}
                                            >
                                                <Form.Item
                                                    name={['relatedGiftStores', gift.id]}
                                                    label={`Cơ sở nhận quà: ${gift.name}`}
                                                    rules={[{ required: true, message: 'Vui lòng chọn ít nhất một cơ sở nhận quà' }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <Select
                                                        mode="multiple"
                                                        placeholder="Chọn một hoặc nhiều cơ sở nhận quà"
                                                        loading={loadingStores}
                                                        options={storeOptions}
                                                        notFoundContent="Quà này chưa được phân bổ cơ sở nào còn dùng được"
                                                    />
                                                </Form.Item>
                                            </div>
                                        );
                                    })}
                                </Space>
                            )}
                        </div>

                        <div className="admin-form-section" ref={buttonsFieldRef}>
                            <h3 className="admin-form-section-title">Nút thao tác</h3>
                            <Form.List name="buttons" initialValue={[]}>
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map((field, index) => (
                                            <div key={field.key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                                <Form.Item
                                                    name={[field.name, 'title']}
                                                    validateTrigger={['onChange', 'onBlur']}
                                                    rules={[
                                                        { required: true, message: 'Vui lòng nhập tiêu đề nút' },
                                                        { max: 20, message: 'Tiêu đề không được vượt quá 20 ký tự' }
                                                    ]}
                                                    style={{ flex: 1, marginBottom: 0 }}
                                                >
                                                    <Input placeholder="Tiêu đề nút" maxLength={20} />
                                                </Form.Item>
                                                <Form.Item
                                                    name={[field.name, 'url']}
                                                    validateTrigger={['onChange', 'onBlur']}
                                                    rules={[
                                                        { required: true, message: 'Vui lòng nhập URL' },
                                                        { type: 'url', message: 'URL không hợp lệ' }
                                                    ]}
                                                    style={{ flex: 2, marginBottom: 0 }}
                                                >
                                                    <Input placeholder="URL (https://...)" />
                                                </Form.Item>
                                                <Button type="text" danger onClick={() => remove(field.name)}>
                                                    <DeleteOutlined />
                                                </Button>
                                            </div>
                                        ))}
                                        {fields.length < 4 && (
                                            <Button
                                                type="dashed"
                                                onClick={() => add()}
                                                block
                                                icon={<PlusOutlined />}
                                                style={{ marginBottom: 16 }}
                                            >
                                                Thêm nút
                                            </Button>
                                        )}
                                    </>
                                )}
                            </Form.List>
                        </div>

                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={submitLoading}
                                disabled={submitLoading}
                                className="admin-button"
                                ref={submitButtonRef}
                            >
                                {editingMessage ? 'Cập nhật' : 'Thêm mới'}
                            </Button>
                        </Form.Item>
                    </Form>
                </div>

                {/* Modal Tour component */}
                <ConfigProvider locale={viVN}>
                    <Tour
                        open={isModalTourOpen}
                        onClose={() => setIsModalTourOpen(false)}
                        steps={modalTourSteps}
                        indicatorsRender={(current, total) => (
                            <span>{current + 1} / {total}</span>
                        )}
                        type="primary"
                        arrow={true}
                        placement="bottom"
                        mask={{
                            style: {
                                boxShadow: 'inset 0 0 15px #333',
                            },
                        }}
                    />
                </ConfigProvider>
            </Modal>

            <Modal
                title="Gửi tin nhắn đến người dùng Zalo"
                visible={sendModalVisible}
                onCancel={() => {
                    setSendModalVisible(false);
                    sendForm.resetFields();
                    setSelectedMessage(null);
                }}
                footer={null}
                className="admin-modal"
            >
                <div className="admin-form-container">
                    <Form
                        form={sendForm}
                        layout="vertical"
                        onFinish={handleSendMessage}
                    >
                        <div className="admin-form-section">
                            <Form.Item
                                name="user_id"
                                label="ID người dùng Zalo"
                                rules={[
                                    { required: true, message: 'Vui lòng nhập ID người dùng Zalo' },
                                    { pattern: /^\d+$/, message: 'ID người dùng Zalo chỉ được chứa số' }
                                ]}
                            >
                                <Input placeholder="Nhập ID người dùng Zalo" />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={sendLoading}
                                    disabled={sendLoading}
                                    className="admin-button"
                                >
                                    Gửi tin nhắn
                                </Button>
                            </Form.Item>
                        </div>
                    </Form>
                </div>
            </Modal>

            {/* Tour component */}
            <ConfigProvider locale={viVN}>
                <Tour
                    open={isTourOpen}
                    onClose={() => setIsTourOpen(false)}
                    steps={tourSteps}
                    indicatorsRender={(current, total) => (
                        <span>{current + 1} / {total}</span>
                    )}
                    type="primary"
                    arrow={true}
                    placement="bottom"
                    mask={{
                        style: {
                            boxShadow: 'inset 0 0 15px #333',
                        },
                    }}
                />
            </ConfigProvider>
        </AdminLayout>
    );
};

export default MessageManagement; 
